/**
 * Copyright 2025 Google LLC. This software is provided as-is, without warranty 
 * or representation for any use or purpose. Your use of it is subject to your 
 * agreement with Google.
 */
import { Component, ElementRef, EventEmitter, Input, Output, Renderer2, ViewEncapsulation, signal } from '@angular/core';
import { GuidelineService } from '../services/guideline.service';
import { Subject, takeUntil } from 'rxjs';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MarkdownComponent } from 'ngx-markdown';
import { FEEDBACK_PLACEHOLDER, THUMBS_UP_OPTIONS, THUMBS_DOWN_OPTIONS, FEEDBACK_ERROR_MSG, ADDITIONAL_FEEDBACK_ERROR_MSG, SORRY_CONTEXT } from '../constants';
@Component({
  selector: 'app-respond',
  standalone: true,
  imports: [NgIf, NgClass, ReactiveFormsModule, TextFieldModule, NgFor, MatIconModule, MatExpansionModule, MatTooltipModule, MarkdownComponent],
  providers: [GuidelineService],
  templateUrl: './respond.component.html',
  styleUrl: './respond.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class RespondComponent {
  feedbackResponse: any;
  botResponseLoader: boolean = true;
  feedbackSent: boolean = false;
  showFeedbackForm: boolean = false;
  isThumbsUpClicked: boolean = false;
  isThumbsDownClicked: boolean = false;
  showThumbsUpOptions: boolean = false;
  showThumbsDownOptions: boolean = false;
  selectedCitation: any;
  showArrowUpIcon: boolean = false;
  enableSubmitBtn: boolean = false;
  markdownContent!: string;
  submitFeedbackLoader: boolean = false;
  showFeedbackErrorMsg: boolean = false;
  feedBackErrorMsg: string = '';
  activeCitation: HTMLElement | null = null;
  readonly panelOpenState = signal(false);
  feedbackForm: FormGroup = this.formBuilder.group({
    feedbackCtrl: [''],
  });
  placeholder: string = FEEDBACK_PLACEHOLDER;
  thumbsUpOptions = THUMBS_UP_OPTIONS
  thumbsDownOptions = THUMBS_DOWN_OPTIONS
  @Input('batchPreviewResponse') batchPreviewResponse: any
  @Input('batchPreviewError') batchPreviewError: any;
  @Input('botMsg') botMsg: any;
  @Input('botError') botError: any;
  @Output() updateCitationVal = new EventEmitter<any>();
  @Output() isPreviousCitationHighlighted = new EventEmitter<boolean>();
  @Output() isSorryContext = new EventEmitter<boolean>();
  private cancelRequest$ = new Subject<void>();

  constructor(private formBuilder: FormBuilder, private elementRef: ElementRef, private renderer: Renderer2, public guidelineServ: GuidelineService) { }

  ngOnInit() {
    this.ngOnChanges();
  }
  ngOnChanges() {
    if (this.botMsg) {
      this.markdownContent = this.processCitations(this.botMsg.answer);
      this.constructBtnLinks(this.botMsg?.inline_citations);
      this.botMsg['cachedPreviewContent'] = new Array(Object.keys(this.botMsg?.inline_citations).length).fill({}); // Initialize the array index starts with 0
    } else if (this.botError) {
      console.log(this.botError)
    }
    this.botResponseLoader = !this.botMsg && !this.botError;
  }

  onCitationClick(event: Event) {
    let target = event.target as HTMLElement;
    if (target && target.tagName === 'SUP' && target.innerHTML) {
      const citationNumber = target.innerHTML;
      this.highlightClass(target)
      this.fetchCitationContent(+citationNumber);
    }
  }

  highlightClass(target: HTMLElement) {
    // remove highlight class for existing active citation
    this.isPreviousCitationHighlighted.emit(true)
    if (this.activeCitation && this.activeCitation !== target) {
      this.renderer.removeClass(this.activeCitation, 'highlight');
    }
    const hasClass = target.classList.contains('highlight');
    if (!hasClass) {
      this.renderer.addClass(target, 'highlight');
      this.activeCitation = target;
    }
  }

  removeHighlightClass() {
    if (this.activeCitation) {
      this.renderer.removeClass(this.activeCitation, 'highlight');
    }
  }

  generateFeedback(rating: number) {
    this.guidelineServ.generateFeedbackResponse(this.botMsg?.session_id, this.botMsg?.qid, rating)
      .pipe(takeUntil(this.cancelRequest$))
      .subscribe({
        next: (res) => {
          this.feedbackResponse = {
            ...this.botMsg,
            "feedback": {
              "response": res,
            }
          }
        },
        error: (error) => {
          this.showFeedbackErrorMsg = true;
          this.feedBackErrorMsg = FEEDBACK_ERROR_MSG;
          this.showFeedbackForm = false;
          if (this.botMsg)
            this.botMsg.feedbackApiError = error.message;
        }
      })
  }

  onOpen() {
    this.panelOpenState.set(true);
    this.showArrowUpIcon = true;
  }

  onClose() {
    this.panelOpenState.set(false);
    this.showArrowUpIcon = false;
  }

  navigateToGuidelineUrl(url: string) {
    window.open(url, '_blank');
  }

  thumbsUp() {
    this.showFeedbackForm = true;
    this.showThumbsUpOptions = true;
    this.showThumbsDownOptions = false;
    this.feedbackSent = false;
    this.clearFeedbackOptions(this.thumbsDownOptions)
    this.generateFeedback(1);
    this.feedbackForm.controls['feedbackCtrl'].setValue("")
  }

  thumbsDown() {
    this.showFeedbackForm = true;
    this.showThumbsDownOptions = true;
    this.showThumbsUpOptions = false;
    this.feedbackSent = false;
    this.clearFeedbackOptions(this.thumbsUpOptions);
    this.feedbackForm.controls['feedbackCtrl'].setValue("")
    this.generateFeedback(0);
  }

  checkFormValidation() {
    this.formValidation()
  }

  formValidation(): boolean {
    if ((this.feedbackForm.controls['feedbackCtrl'].value.length > 0) || (this.enableSubmitBtn)) {
      return false
    } else if ((this.feedbackForm.controls['feedbackCtrl'].value.length > 0)) {
      return false
    } else if ((this.enableSubmitBtn)) {
      return false
    }
    else return true
  }

  submitFeedback(comment: any) {
    this.submitFeedbackLoader = true;
    let rating;
    let options;
    if (this.showThumbsDownOptions) {
      rating = 0;
      options = this.getSelectedFeedbackOptions(this.thumbsDownOptions);
    } else {
      rating = 1
      options = this.getSelectedFeedbackOptions(this.thumbsUpOptions);
    }
    this.feedbackResponse = {
      ...this.botMsg, "feedback": {
        "option": options,
        "comment": comment,
        ...this.feedbackResponse?.feedback
      }
    }
    let appendOptionsAndComment = '[' + options.toString() + ']; ' + comment;
    this.guidelineServ.generateFeedbackComment(this.botMsg?.session_id, this.botMsg?.qid, appendOptionsAndComment, rating)
      .pipe(takeUntil(this.cancelRequest$)).subscribe({
        next: (res) => {
          this.feedbackSent = true;
          this.submitFeedbackLoader = false;
          this.clearFeedbackform();
        },
        error: (error) => {
          this.submitFeedbackLoader = false;
          this.feedBackErrorMsg = ADDITIONAL_FEEDBACK_ERROR_MSG
          this.clearFeedbackform();
          this.showFeedbackErrorMsg = true;
          if (this.botMsg)
            this.botMsg.feedbackApiError = error.message;
        }
      })
  }

  clearFeedbackform() {
    this.showFeedbackForm = false;
    this.clearFeedbackOptions(this.thumbsDownOptions);
    this.clearFeedbackOptions(this.thumbsUpOptions);
    this.showThumbsUpOptions = false;
    this.showThumbsDownOptions = false
  }

  closeFeedbackErrorMsg() {
    this.showFeedbackErrorMsg = false;
    this.botMsg.feedbackApiError = '';
    this.showThumbsUpOptions = false;
    this.showThumbsDownOptions = false;
  }

  toggleSelectedOptions(option: any, optionsArray: any) {
    optionsArray.filter((val: any) => {
      if (val.name === option.name) {
        val.selected = !val.selected;
      }
    });
    this.toggleSubmitBtn(optionsArray)
  }

  toggleSubmitBtn(optionsArray: any) {
    const selected = (element: any) => element.selected === true;
    if (optionsArray.some(selected)) {
      this.enableSubmitBtn = true;
    } else {
      this.enableSubmitBtn = false;
    }
  }

  clearFeedbackOptions(clearOptions: any) {
    clearOptions.filter((val: any) => {
      if (val.selected) {
        val.selected = false;
        this.enableSubmitBtn = false;
      }
    })
  }

  getSelectedFeedbackOptions(optionsArray: any) {
    return optionsArray.filter((c: any) => c.selected).map((c: any) => c.name);
  }

  closeFeedback() {
    this.showFeedbackForm = false;
    this.feedbackSent = false;
  }

  processCitations(text: any) {
    let noCitationFound = false;
    let sorryContext = false;
    if (text.includes(SORRY_CONTEXT)) {
      sorryContext = true;
    }
    let citations = Object.keys(this.botMsg?.inline_citations);
    if (!citations.length) {
      noCitationFound = true;
    }
    else {
      for (let i = 1; i <= citations.length; i++) {
        let pos = text.indexOf("[" + i + "]");
        let substr = text.substr(pos, (i.toString().length) + 2);
        text = text.replace(substr, this.createCitationButton(i));
      }
    }
    if (sorryContext && noCitationFound) {
      this.isSorryContext.emit(true);
    }
    return text;
  }

  constructBtnLinks(inline_citations: any[]) {
    for (let i = 1; i <= Object.keys(inline_citations).length; i++) {
      let btnId = 'citation' + i;
      const btnElement = this.elementRef.nativeElement.querySelector(`[id = ${btnId}]`);
      if (btnElement) {
        this.hookupBtnLink(btnElement, i);
      }
    }
  }

  hookupBtnLink(btnElement: any, i: number) {
    this.renderer?.listen(btnElement, 'click', () => {
      this.emitCitationContent('', false, true);
      this.fetchCitationContent(i);
    });
  }

  createCitationButton(i: number) {
    let btnId = 'citation' + i;
    let citation = `<span><sup class='citation'><button id=${btnId} type='button'>${i}</button></sup></span>`
    return citation;
  }

  displayToolTipInfo(info: string) {
    return info.length >= 64 ? info : null
  }

  emitCitationContent(value: any, isError: boolean, isLoading: boolean) {
    let content = {
      'content': value,
      'isError': isError,
      'isLoading': isLoading
    }
    this.updateCitationVal.emit(content);
  }
  fetchCitationContent(index: number) {
    this.emitCitationContent('', false, true);
    // cachedPreviewContent array index starts with 0 
    if (this.botMsg?.cachedPreviewContent[index - 1]?.value != undefined) { // check for cached preview content availability
      let value = this.botMsg.cachedPreviewContent[index - 1].value;
      this.emitCitationContent(value, false, false)
    }
    else {
      //make api call when cached preview content is not available
      // Collect ALL entries for this citation (multiple vais_content sources may exist)
      const citationEntries = this.botMsg?.inline_citations[index] || [];
      
      if (!citationEntries || citationEntries.length === 0) {
        console.error(`No citation entries found for index ${index}`);
        this.emitCitationContent('', true, false);
        return;
      }
      
      const ch_ids = citationEntries.map((entry: any) => entry.ch_id).filter((id: string) => id);
      const vais_contents = citationEntries.map((entry: any) => entry.vais_content).filter((content: string) => content);
      
      // Use first entry for metadata (cit_content, relevance_score, uri are same across all entries)
      this.selectedCitation = citationEntries[0];
      
      // Always send as arrays
      this.guidelineServ.generatePreviewContent(ch_ids, this.selectedCitation.cit_content, this.selectedCitation.relevance_score, this.selectedCitation.uri, vais_contents)
        .pipe(takeUntil(this.cancelRequest$)).subscribe({
          next: ((res: any) => {
            let obj = {
              value: res
            }
            this.botMsg.cachedPreviewContent[index - 1] = obj;
            this.emitCitationContent(res, false, false);
          }),
          error: ((error: any) => {
            this.emitCitationContent('', true, false);
          })
        })
    }
  }
  cancelRequest() {
    this.cancelRequest$.next();  //  Cancels the request
  }
  ngOnDestroy() {
    this.cancelRequest(); // prevent memory leaks
    this.cancelRequest$.complete();
  }
}
